import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { useMap } from '@vis.gl/react-google-maps';
import { useState } from 'react';

import { buttonVariants } from '@/components/ui/button';
import { LoadingSwap } from '@/components/ui/loading-swap';

import type { MapLocateControlProps } from '.';

/**
 * A control that requests the user's current location via the Geolocation API.
 *
 * - Invokes [onLocated] with the resolved [LatLngLiteral].
 * - Displays a loading state while the position is being fetched.
 *
 * Props extend the app's [Button] component for consistent styling.
 */
export function MapLocateControl(mapLocateControlProps: MapLocateControlProps) {
  const { onLocated, asChild = false, variant, variantColor, size, className, children, ...props } = mapLocateControlProps;
  const map = useMap();
  const [loading, setLoading] = useState<boolean>(false);

  const handleLocate = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        map?.panTo(pos);
        if (map && (map.getZoom() ?? 0) < 14) {
          map.setZoom(14);
        }
        onLocated?.(pos);
        setLoading(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const Comp = asChild ? Slot : 'button';

  return (
    <Comp data-slot="navigate-to-user-location" type="button" onClick={handleLocate} disabled={loading} className={cn(buttonVariants({ variant, variantColor, size, className }))} {...props}>
      <LoadingSwap isLoading={loading} className="flex items-center gap-2">
        {children}
      </LoadingSwap>
    </Comp>
  );
}
